// tabs/ThinkyBaaSNetwork.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNetworkWired, faCheckCircle, faServer, faPlay } from "@fortawesome/free-solid-svg-icons";

const dummyNetworks = [
  {
    id: 1,
    name: "Blockchain Node Network",
    description: "Simulates deploying nodes across multiple regions for redundancy.",
    deployed: false,
    synchronized: false,
    verified: false,
    log: [],
    precondition: true,
  },
  {
    id: 2,
    name: "Edge IoT Network",
    description: "Integrates IoT edge devices to feed real-time data.",
    deployed: false,
    synchronized: false,
    verified: false,
    log: [],
    precondition: false,
  },
  {
    id: 3,
    name: "Cloud Infrastructure",
    description: "Hosts blockchain components with secure and scalable architecture.",
    deployed: false,
    synchronized: false,
    verified: false,
    log: [],
    precondition: true,
  },
];

const ThinkyBaaSNetwork = () => {
  const [networks, setNetworks] = useState(dummyNetworks.map(n => ({ ...n, log: n.log || [] })));

  // Deploy network
  const deployNetwork = (id) => {
    setNetworks(prev =>
      prev.map(n =>
        n.id === id && !n.deployed
          ? { ...n, deployed: true, log: [...(n.log || []), { event: "Network Deployed", time: new Date().toLocaleTimeString() }] }
          : n
      )
    );
  };

  // Synchronize network
  const synchronizeNetwork = (id) => {
    setNetworks(prev =>
      prev.map(n => {
        if (n.id === id) {
          if (!n.precondition) {
            return { ...n, log: [...(n.log || []), { event: "Synchronization blocked: Precondition not met", time: new Date().toLocaleTimeString() }] };
          }
          return { ...n, synchronized: true, log: [...(n.log || []), { event: "Network Synchronized", time: new Date().toLocaleTimeString() }] };
        }
        return n;
      })
    );
  };

  // Verify network
  const verifyNetwork = (id) => {
    setNetworks(prev =>
      prev.map(n =>
        n.id === id && n.synchronized && !n.verified
          ? { ...n, verified: true, log: [...(n.log || []), { event: "Network Verified", time: new Date().toLocaleTimeString() }] }
          : n
      )
    );
  };

  return (
    <div className="bg-gray-900 p-6 rounded-2xl shadow-lg space-y-6">
      <div className="flex items-center mb-4">
        <FontAwesomeIcon icon={faNetworkWired} className="text-blue-400 text-xl lg:text-2xl mr-4" />
        <h2 className="text-lg lg:text-xl font-bold">Network & Infrastructure Workflow</h2>
      </div>

      <p className="text-gray-300 text-sm">
        This page simulates a blockchain <span className="font-semibold">Network & Infrastructure workflow</span>:
        Deploy → Synchronize → Verify.
      </p>

      <div className="space-y-4">
        {networks.map(n => (
          <div key={n.id} className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <h3 className="text-xl font-semibold mb-1">{n.name}</h3>
            <p className="text-gray-400 mb-2">{n.description}</p>

            <div className="flex flex-wrap gap-2 mb-2">
              <span className={`px-2 py-1 rounded-sm text-sm ${n.deployed ? "bg-green-600" : "bg-yellow-600"}`}>
                {n.deployed ? "Deployed ✅" : "Pending Deployment ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${n.synchronized ? "bg-green-600" : "bg-yellow-600"}`}>
                {n.synchronized ? "Synchronized ✅" : "Pending Synchronization ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${n.verified ? "bg-green-600" : "bg-yellow-600"}`}>
                {n.verified ? "Verified ✅" : "Pending Verification ⏳"}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                disabled={n.deployed}
                onClick={() => deployNetwork(n.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  n.deployed ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faServer} className="mr-1" /> Deploy
              </button>
              <button
                disabled={!n.deployed || n.synchronized}
                onClick={() => synchronizeNetwork(n.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !n.deployed || n.synchronized ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faPlay} className="mr-1" /> Synchronize
              </button>
              <button
                disabled={!n.synchronized || n.verified}
                onClick={() => verifyNetwork(n.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !n.synchronized || n.verified ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Verify
              </button>
            </div>

            <div className="mt-3 bg-gray-900 p-2 rounded-lg border border-gray-700">
              <h4 className="text-gray-300 font-semibold mb-1">Event Log:</h4>
              {(!n.log || n.log.length === 0) ? (
                <p className="text-gray-500 text-sm">No events yet.</p>
              ) : (
                <ul className="text-gray-400 text-sm list-disc list-inside space-y-1">
                  {n.log?.map((log, idx) => (
                    <li key={idx}>
                      [{log.time}] {log.event}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThinkyBaaSNetwork;
